import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  CircularProgress,
  Divider,
  Stack,
  Avatar,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  AccountTree as AccountTreeIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Badge as BadgeIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Gavel as GavelIcon,
  Computer as ComputerIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import PageWithHeader from '../common/PageWithHeader';
import {
  useGetJobDescriptionByIdQuery,
  useGetJobDescriptionReportsCountsQuery,
} from '../../redux/jobDescriptionApi';

// Helper to get icon for BSC perspective
const getPerspectiveIcon = (name: string) => {
  switch (name.toLowerCase()) {
    case 'financial':
      return <MoneyIcon />;
    case 'customer':
      return <PeopleIcon />;
    case 'internal processes':
      return <SettingsIcon />;
    case 'learning & growth':
      return <TrendingUpIcon />;
    default:
      return <AssessmentIcon />;
  }
};

// Helper to get color for BSC perspective
const getPerspectiveColor = (name: string): 'primary' | 'success' | 'warning' | 'info' => {
  switch (name.toLowerCase()) {
    case 'financial':
      return 'success';
    case 'customer':
      return 'primary';
    case 'internal processes':
      return 'warning';
    case 'learning & growth':
      return 'info';
    default:
      return 'primary';
  }
};

// Helper to get icon for authority category
const getAuthorityIcon = (category: string) => {
  switch (category) {
    case 'Financial':
    case 'Procurement':
      return <MoneyIcon fontSize="small" />;
    case 'HR/Personnel':
      return <GroupsIcon fontSize="small" />;
    case 'Operational':
      return <SettingsIcon fontSize="small" />;
    case 'Legal/Contracts':
      return <GavelIcon fontSize="small" />;
    case 'IT/Technology':
      return <ComputerIcon fontSize="small" />;
    default:
      return <WorkIcon fontSize="small" />;
  }
};

const JobDescriptionView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const { data: jd, isLoading, error } = useGetJobDescriptionByIdQuery(
    { id: id! },
    { skip: !id }
  );
  const { data: reportsCounts } = useGetJobDescriptionReportsCountsQuery(
    { id: id! },
    { skip: !id }
  );
  
  if (isLoading) {
    return (
      <PageWithHeader>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </PageWithHeader>
    );
  }
  
  if (error || !jd) {
    return (
      <PageWithHeader>
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h6" color="error" gutterBottom>
              Job Description not found
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/reviews/job-descriptions')}
              sx={{ mt: 2 }}
            >
              Back to List
            </Button>
          </Paper>
        </Box>
      </PageWithHeader>
    );
  }
  
  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Hero Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            borderRadius: 4,
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton
                onClick={() => navigate('/reviews/job-descriptions')}
                sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: 'primary.main',
                  fontSize: '1.5rem',
                }}
              >
                {jd.title?.charAt(0) || 'J'}
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="bold" color="primary.dark">
                  {jd.title}
                </Typography>
                {jd.assignedUserName && (
                  <Typography variant="h6" color="primary.main" fontWeight="500" mt={0.5}>
                    {jd.assignedUserName}
                  </Typography>
                )}
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={1}>
                  {jd.grade && (
                    <Chip
                      label={`Grade ${jd.grade}`}
                      color="secondary"
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                  <Chip
                    label={jd.isActive ? 'Active' : 'Inactive'}
                    color={jd.isActive ? 'success' : 'default'}
                    size="small"
                  />
                  {jd.department && (
                    <Chip
                      label={jd.department}
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/reviews/job-descriptions/${id}/edit`)}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Edit Job Description
            </Button>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* Job Purpose Card */}
          {jd.jobPurpose && (
            <Grid item xs={12}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <WorkIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">Job Purpose</Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" lineHeight={1.7}>
                    {jd.jobPurpose}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Accountability & Reports Section */}
          <Grid item xs={12} lg={6}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <AccountTreeIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">Accountability</Typography>
                </Box>
                
                {/* Reports To */}
                {jd.reportsTo && (
                  <Box display="flex" alignItems="center" gap={2} mb={3} p={2} bgcolor="grey.50" borderRadius={2}>
                    <Avatar sx={{ bgcolor: 'primary.light' }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Reports To</Typography>
                      <Typography variant="body1" fontWeight="medium">{jd.reportsTo}</Typography>
                    </Box>
                  </Box>
                )}

                {/* Report Counts */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha('#1976d2', 0.08),
                        borderRadius: 2,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="h3" color="primary" fontWeight="bold">
                        {reportsCounts?.directReportsCount ?? jd.directReportsCount ?? 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Direct Reports</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha('#9c27b0', 0.08),
                        borderRadius: 2,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="h3" color="secondary" fontWeight="bold">
                        {reportsCounts?.indirectReportsCount ?? jd.indirectReportsCount ?? 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Indirect Reports</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Authority Limits */}
          <Grid item xs={12} lg={6}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <GavelIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">Limits to Authority</Typography>
                </Box>

                {jd.limitsToAuthority && jd.limitsToAuthority.length > 0 ? (
                  <Stack spacing={2}>
                    {jd.limitsToAuthority.filter(l => l.category || l.description).map((limit, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 2,
                          bgcolor: 'grey.50',
                          borderRadius: 2,
                          borderLeft: 4,
                          borderColor: 'primary.main',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          {getAuthorityIcon(limit.category)}
                          <Chip label={limit.category} size="small" color="primary" variant="outlined" />
                        </Box>
                        <Typography variant="body2" fontWeight="medium" mb={1}>
                          {limit.description}
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {(limit.category === 'Financial' || limit.category === 'Procurement') && limit.monetaryLimit && (
                            <Chip
                              size="small"
                              label={`${limit.currency || 'USD'} ${limit.monetaryLimit.toLocaleString()}`}
                              color="success"
                            />
                          )}
                          {limit.category === 'HR/Personnel' && limit.headcountLimit && (
                            <Chip size="small" label={`${limit.headcountLimit} positions`} color="info" />
                          )}
                          {limit.category === 'HR/Personnel' && limit.salaryBandLimit && (
                            <Chip size="small" label={limit.salaryBandLimit} color="secondary" />
                          )}
                          {limit.category === 'Operational' && limit.unitLimit && (
                            <Chip size="small" label={`${limit.unitLimit} ${limit.unitType || 'units'}`} color="warning" />
                          )}
                          {limit.category === 'Legal/Contracts' && limit.contractDurationLimit && (
                            <Chip size="small" label={limit.contractDurationLimit} color="info" />
                          )}
                          {limit.category === 'Legal/Contracts' && limit.contractValueLimit && (
                            <Chip size="small" label={`$${limit.contractValueLimit.toLocaleString()}`} color="success" />
                          )}
                          {limit.category === 'IT/Technology' && limit.accessLevel && (
                            <Chip size="small" label={limit.accessLevel} color="primary" />
                          )}
                          {limit.category === 'IT/Technology' && limit.systemScope && (
                            <Chip size="small" label={limit.systemScope} variant="outlined" />
                          )}
                          {limit.approvalRequired && (
                            <Chip
                              size="small"
                              label={`Approval: ${limit.approvalRequired}`}
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary" fontStyle="italic">
                    No authority limits defined
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* BSC Perspectives & KRAs */}
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <AssessmentIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">BSC Perspectives & Key Result Areas</Typography>
                </Box>
                
                <Grid container spacing={2}>
                  {jd.bscPerspectives?.map((perspective, pIndex) => {
                    const color = getPerspectiveColor(perspective.name);
                    // Handle both string[] and object[] formats for kras
                    const kraItems = perspective.kras?.map((kra: string | { objective?: string; description?: string }) => 
                      typeof kra === 'string' ? kra : (kra.objective || kra.description || '')
                    ).filter(Boolean) || [];
                    
                    return (
                      <Grid item xs={12} md={6} key={pIndex}>
                        <Card
                          variant="outlined"
                          sx={{
                            height: '100%',
                            borderColor: `${color}.main`,
                            borderWidth: 2,
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: 2,
                              borderColor: `${color}.dark`,
                            },
                          }}
                        >
                          <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                              <Avatar
                                sx={{
                                  bgcolor: `${color}.main`,
                                  width: 36,
                                  height: 36,
                                }}
                              >
                                {getPerspectiveIcon(perspective.name)}
                              </Avatar>
                              <Typography variant="subtitle1" fontWeight="bold" color={`${color}.dark`}>
                                {perspective.name}
                              </Typography>
                            </Box>
                            
                            {kraItems.length > 0 ? (
                              <Box display="flex" flexWrap="wrap" gap={1}>
                                {kraItems.map((kra, kIndex) => (
                                  <Chip
                                    key={kIndex}
                                    label={kra}
                                    color={color}
                                    variant="outlined"
                                    size="small"
                                    sx={{ maxWidth: '100%' }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                No KRAs defined for this perspective
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Required Competencies */}
                {jd.requiredCompetencies && jd.requiredCompetencies.length > 0 && (
                  <Box mt={4}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                      <StarIcon sx={{ verticalAlign: 'middle', mr: 1, color: 'warning.main' }} />
                      Required Competencies
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {jd.requiredCompetencies.map((comp, index) => (
                        <Chip
                          key={index}
                          icon={<StarIcon />}
                          label={comp}
                          color="warning"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Inherent Job Requirements */}
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <SchoolIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">Inherent Job Requirements</Typography>
                </Box>

                <Grid container spacing={3}>
                  {/* Qualifications */}
                  {jd.minimumQualifications && jd.minimumQualifications.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" mb={2}>
                        MINIMUM QUALIFICATIONS
                      </Typography>
                      <Stack spacing={1}>
                        {jd.minimumQualifications.map((qual, index) => (
                          <Box
                            key={index}
                            display="flex"
                            alignItems="flex-start"
                            gap={1}
                            p={1.5}
                            bgcolor="grey.50"
                            borderRadius={1}
                          >
                            <CheckIcon
                              color={qual.isRequired ? 'primary' : 'action'}
                              fontSize="small"
                              sx={{ mt: 0.25 }}
                            />
                            <Box flex={1}>
                              <Typography variant="body2" fontWeight="medium">
                                {qual.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {qual.level}{qual.field ? ` in ${qual.field}` : ''}
                              </Typography>
                            </Box>
                            <Chip
                              label={qual.isRequired ? 'Required' : 'Preferred'}
                              size="small"
                              color={qual.isRequired ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Grid>
                  )}

                  {/* Professional Registrations */}
                  {jd.professionalRegistrations && jd.professionalRegistrations.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" mb={2}>
                        PROFESSIONAL REGISTRATIONS
                      </Typography>
                      <Stack spacing={1}>
                        {jd.professionalRegistrations.map((reg, index) => (
                          <Box
                            key={index}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            p={1.5}
                            bgcolor="grey.50"
                            borderRadius={1}
                          >
                            <BadgeIcon color="primary" fontSize="small" />
                            <Box flex={1}>
                              <Typography variant="body2" fontWeight="medium">
                                {reg.body}
                              </Typography>
                              {reg.membershipType && (
                                <Typography variant="caption" color="text.secondary">
                                  {reg.membershipType}
                                </Typography>
                              )}
                            </Box>
                            <Chip
                              label={reg.isRequired ? 'Required' : 'Preferred'}
                              size="small"
                              color={reg.isRequired ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Grid>
                  )}

                  {/* Experience */}
                  {(jd.minimumExperienceYears || jd.minimumExperienceDescription) && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" mb={2}>
                        EXPERIENCE REQUIRED
                      </Typography>
                      <Box p={2} bgcolor="primary.50" borderRadius={2}>
                        {jd.minimumExperienceYears && (
                          <Chip
                            label={jd.minimumExperienceYears}
                            color="primary"
                            sx={{ mb: 1, fontWeight: 'bold' }}
                          />
                        )}
                        {jd.minimumExperienceDescription && (
                          <Typography variant="body2" color="text.secondary">
                            {jd.minimumExperienceDescription}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}

                  {/* Job Knowledge */}
                  {jd.minimumJobKnowledge && jd.minimumJobKnowledge.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" mb={2}>
                        JOB-RELATED KNOWLEDGE
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {jd.minimumJobKnowledge.map((knowledge, index) => (
                          <Chip key={index} label={knowledge} variant="outlined" size="small" />
                        ))}
                      </Box>
                    </Grid>
                  )}

                  {/* Other Requirements */}
                  {jd.otherRequirements && jd.otherRequirements.length > 0 && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" mb={2}>
                        OTHER REQUIREMENTS
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {jd.otherRequirements.map((req, index) => (
                          <Chip key={index} label={req} variant="outlined" color="secondary" size="small" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Footer Metadata */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
          px={2}
          py={1}
          bgcolor="grey.50"
          borderRadius={2}
        >
          <Typography variant="caption" color="text.secondary">
            Created: {jd.createdAt ? new Date(jd.createdAt).toLocaleDateString() : 'N/A'}
            {jd.updatedAt && ` • Updated: ${new Date(jd.updatedAt).toLocaleDateString()}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {jd.id}
          </Typography>
        </Box>
      </Box>
    </PageWithHeader>
  );
};

export default JobDescriptionView;
